import { cn } from "@faire/react/tools/cn";
import { PopoverTrigger } from "@faire/slate/private/popover/PopoverTrigger";
import React, { isValidElement, cloneElement } from "react";

type IProps = React.HTMLProps<HTMLElement> & {
  /**
   * When true, makes TooltipTrigger behaves as a Toggletip Trigger.
   * It automaticallywraps the children in a button element.
   *
   * Toggletips are a special tooltip that is triggered by clicking or pressing a key,
   * while hovering or focusing on the trigger element doesn't open it.
   */
  addInteraction?: boolean;
  /** Applies dotted-underline styling to the trigger element. */
  dottedUnderline?: boolean;
};

export const TooltipTrigger: React.FC<IProps> = ({
  "aria-label": ariaLabel,
  addInteraction,
  dottedUnderline,
  children,
  ...props
}) => {
  if (!addInteraction && !dottedUnderline) {
    return <PopoverTrigger {...props}>{children}</PopoverTrigger>;
  }

  if (!isValidElement(children)) {
    // oxlint-disable-next-line no-console
    console.error("TooltipTrigger must have a valid child element");
    return null;
  }

  const childrenProps = children.props as Record<string, unknown>;

  return (
    <PopoverTrigger {...props}>
      {cloneElement(children, {
        ...childrenProps,
        ...(addInteraction
          ? {
              "aria-label": ariaLabel,
              "aria-hidden": false,
              role: undefined,
              tabIndex: 0,
            }
          : {}),
        className: cn(
          childrenProps.className as string | undefined,
          dottedUnderline && "fs-tooltip__trigger--dotted-underline"
        ),
      } as Record<string, unknown>)}
    </PopoverTrigger>
  );
};
